/**
 * 데모 페이지 공통 네비게이션 컴포넌트
 * 모든 데모 페이지 상단에 표시되어 페이지 간 이동을 지원
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  UserGroupIcon,
  ClockIcon,
  ClipboardDocumentCheckIcon,
  CalendarDaysIcon,
  BellAlertIcon,
  CheckCircleIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  PresentationChartLineIcon,
  BookOpenIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';

interface DemoPage {
  title: string;
  path: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const DEMO_PAGES: DemoPage[] = [
  { title: '회원 관리', path: '/demo/members', icon: UserGroupIcon },
  { title: '입출입 기록', path: '/demo/entrance-exit', icon: ClockIcon },
  { title: '상담 관리', path: '/demo/consultation', icon: ClipboardDocumentCheckIcon },
  { title: '상담 대시보드', path: '/demo/consultation-dashboard', icon: ChartBarIcon },
  { title: '목표 관리', path: '/demo/goal-management', icon: ShieldCheckIcon },
  { title: '학습 분석', path: '/demo/study-analytics', icon: PresentationChartLineIcon },
  { title: '과목 관리', path: '/demo/subject-management', icon: BookOpenIcon },
  { title: '출석 스케줄 설정', path: '/demo/attendance/schedule', icon: CalendarDaysIcon },
  { title: '미출석 모니터링', path: '/demo/attendance/monitoring', icon: BellAlertIcon },
  { title: '통합 레이아웃 관리', path: '/demo/integrated-layout', icon: CheckCircleIcon },
  { title: '게이트 관리', path: '/demo/gates', icon: CheckCircleIcon },
  { title: '비즈니스 통계', path: '/demo/business', icon: ChartBarIcon },
];

interface DemoNavigationProps {
  currentTitle: string;
  currentIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const DemoNavigation: React.FC<DemoNavigationProps> = ({ currentTitle, currentIcon: CurrentIcon }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // 현재 페이지를 제외한 다른 페이지들
  const otherPages = DEMO_PAGES.filter((page) => page.path !== location.pathname);

  return (
    <header className="bg-white shadow-sm sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* 랜딩 페이지로 돌아가기 버튼 */}
          <button
            onClick={() => navigate('/landing')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>랜딩 페이지로</span>
          </button>

          {/* 현재 페이지 제목 */}
          <h1 className="text-xl font-bold text-gray-900 flex items-center">
            <CurrentIcon className="h-6 w-6 mr-2 text-blue-600" />
            {currentTitle}
          </h1>

          {/* 다른 데모 페이지 드롭다운 */}
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <span>다른 페이지</span>
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isDropdownOpen && (
              <>
                {/* 드롭다운 배경 (클릭 시 닫기) */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsDropdownOpen(false)}
                ></div>

                {/* 드롭다운 메뉴 */}
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20">
                  {otherPages.map((page, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        navigate(page.path);
                        setIsDropdownOpen(false);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3"
                    >
                      <page.icon className="h-5 w-5 text-blue-600" />
                      <span className="text-gray-900">{page.title}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default DemoNavigation;
